import { Card, CardBody, Button, Container, Row, Col, CardHeader, Modal, Spinner } from "reactstrap";
import { useParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { fetchQuoteRevisionsDropdown } from '../../quotes/data/quoteRevisionService';
import { fetchOpportunityById } from '../../opportunities/data/opportunitiesService';
import { finaliseQuote, exportQuoteFile, getOpportunityLiveFiles, hasGeneratedQuote } from '../../sharepoint/data/sharepointService';
import { toast } from 'react-toastify';
import { fetchExportTemplateDropdown, exportTemplate, fetchExportTemplatesToRunOnQuoteCreation, fetchExportTemplateById } from "../../exportTemplate/data/exportTemplateService";
import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import CustomMenuList from '../../../components/utility/CustomMenuList';
import Select from 'react-select';
import { useTheme } from '../../../hooks/useTheme';
import { downloadFile } from "../../../utils/fileUtils";
import DataLoader from "../../../components/loader/DataLoader";
import QuoteReviewWarnings from "./quoteReviewWarnings";
import QuoteReviewErrors from "./quoteReviewErrors";
import { getItemErrors } from "../functions/quoteFunctions";
import { createAudit } from "../../../features/audit/data/auditService";
import { generateQuote } from "../../quoteTemplate/data/quoteTemplateService";
import { useQuoteRequestData } from "../../../hooks/useQuoteRequestData";
import { QuoteDataContext } from "../contexts/quoteDataContext";
import { useLockState } from "../../../components/userLocks/LockCheck";
import { getQuoteFileName } from "../functions/quoteFunctions";
import QuoteLiveFilesDialog from "./quoteLiveFilesDialog";

export default function QuoteReview({
    quoteRevision,
    quoteItems,
    quoteModules,
    quoteCustomisation,
    onAddWarning,
    onRemoveWarning,
    onAcknowledgedWarningChange,
    onApprovedWarningChange,
    lockRevision,
    isBasketProcessingActive = false,
    isActive
}) {
    const theme = useTheme();
    const { id: opportunityId, quoteId, quoteRevisionId } = useParams();
    const revisionId = Number(quoteRevisionId);

    const { itemLookupTypes, suppliers, salesforceCodes } = useContext(QuoteDataContext);

    const defaults = useMemo(() => ({
        templateId: 0
    }), []);

    const [exporting, setExporting] = useState(false);
    const [form, setForm] = useState(defaults);
    const [isGenerateFinalQuoteButtonEnabled, setIsGenerateFinalQuoteButtonEnabled] = useState(true);
    const [isGeneratingFinalQuote, setIsGeneratingFinalQuote] = useState(false);
    const [quoteHasBeenGenerated, setQuoteHasBeenGenerated] = useState(false);
    const { salesForceData, customContent, exportData, isLoadingOpportunity, isLoadingTemplate, templatesData } = useQuoteRequestData(quoteRevision, quoteItems, quoteCustomisation);
    const lockState = useLockState();
    const [isSharepointFilesDialogOpened, setIsSharepointFilesDialogOpened] = useState(false);
    const [liveFiles, setLiveFiles] = useState([]);
    const [isReadingSharepointFiles, setIsReadingSharepointFiles] = useState(false);
    const isRegenerationRef = useRef(false);
    const hasGeneratedQuoteRef = useRef(false);

    useEffect(() => {
        setQuoteHasBeenGenerated(false);
    }, [revisionId]);

    const warnings = quoteRevision.quoteOrderInformation?.warnings || [];

    const getRevisions = async () => {
        const revisions = await fetchQuoteRevisionsDropdown(quoteId);
        return revisions;
    }

    const {
        data: revisions = [],
        isLoading: revisionsLoading
    } = useQuery({
        queryKey: ["quoteRevisionsDropdown", quoteId],
        queryFn: getRevisions,
        enabled: !!quoteId,
        staleTime: 60000
    });

    const revision = !revisionsLoading && revisions.find(f => f.id === revisionId)
    const mainRevision = !revisionsLoading && revisions.find(f => f.isMain);
    const revisionIsLocked = revision?.isLocked || quoteHasBeenGenerated;

    const getOpportunity = async () => {
        const opportunity = await fetchOpportunityById(opportunityId);
        return opportunity?.records;
    }

    const {
        data: opportunity = {},
        isLoading: opportunityLoading
    } = useQuery({
        queryKey: ["opportunityData", opportunityId],
        queryFn: getOpportunity,
        enabled: !!opportunityId,
        staleTime: 5 * 60_000, // 5 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const finalise = async (file, updateLiveFolder) => {
        const isRegeneration = isRegenerationRef.current;
        const form = new FormData();
        form.append('file', file);
        form.append('sharepoint', opportunity.SharePoint_Folder__c);
        form.append('quoteId', quoteId);
        form.append('revisionNumber', mainRevision.revisionNumber);
        form.append('isRegeneration', isRegeneration);
        form.append('isActiveQuote', isActive);
        form.append('updateLiveFolder', updateLiveFolder);
        const { message, responseOk } = await finaliseQuote(form);
        if (!responseOk) {
            const errorMessage = message.startsWith("A file is currently locked") ? message : `${message} ${file.name}`
            toast.error(errorMessage);
            console.error(`Quote ${quoteId} revision ${mainRevision.revisionNumber} - ${errorMessage}`);
            await createAudit({ "action": "Finalise sharepoint quote", "result": "Error", "source": `quote.${quoteId}`, "payload": JSON.stringify({ message: errorMessage }) });
            downloadFile(file, file.name);
        }
        else {
            let auditMessage;
            if (isActive) {
                if (isRegeneration && !hasGeneratedQuoteRef.current) {
                    auditMessage = "The LIVE folder has not been updated because the generated document could not be found in Sharepoint";                    
                } else {
                    auditMessage = updateLiveFolder ? "The LIVE folder has been updated by user choice" : "The LIVE folder has not been updated by user choice";                    
                }
            } else {
                auditMessage = "The LIVE folder has not been updated because the quote is not active";
            }
            await createAudit({ "action": "Finalise sharepoint quote", "result": "Successful", "source": `quote.${quoteId}`, "payload": JSON.stringify({ isRegeneration, message: auditMessage }) });
        }
        return { message, responseOk };
    }

    const generateFinalQuote = async (updateLiveFolder) => {
        if (isLoadingOpportunity) return;

        // disable the 'generate final quote' button to prevent multiple clicks
        setIsGenerateFinalQuoteButtonEnabled(false);

        setIsSharepointFilesDialogOpened(false);

        // display the loading modal
        setIsGeneratingFinalQuote(true);

        try {
            // export all the Excel templates flagged with 'RunOnQuoteCreation' to Sharepoint
            if (!await exportExcelTemplatesToSharepoint()) {
                console.error("Error exporting Excel templates to Sharepoint");
                // return;
            }

            // generate/upload the Word file to Sharepoint + set the Sharepoint link in the finaliseResult.message variable
            const { quoteExported, sentToSharepoint, sharepointURL } = await generateQuoteAndFinalise(updateLiveFolder)
            if (!quoteExported) {
                console.error(`Error ${isRegenerationRef.current ? 'regenerating' : 'generating'} final quote and uploading to Sharepoint:`);
                return;
            }

            if (sentToSharepoint) {
                // open the generated word template in a new browser tab
                const newTab = window.open(sharepointURL, '_blank', 'noopener,noreferrer');
                if (newTab) {
                    window.focus();
                }

                console.log(`✅ Module: Quote ${isRegenerationRef.current ? 'regeneration' : 'generation'}. Result: final quote ${isRegenerationRef.current ? 'regenerated' : 'generated'}. Quote: ${quoteId}. The LIVE folder ${updateLiveFolder ? 'has' : 'has not'} been updated.`);
            }

            // lock the current revision (skipped when regenerating)
            if (!isRegenerationRef.current) {
                const lockSuccessful = await lockRevision();
                if (!lockSuccessful) return;
                setQuoteHasBeenGenerated(true);
            }
        } finally {
            setIsGenerateFinalQuoteButtonEnabled(true);
            setIsGeneratingFinalQuote(false);
        }
    }

    async function getExportTemplateFileNames() {
        let parsedExportTemplateFileNames = null;
        if (exportTemplatesToRunOnQuoteCreationData.length > 0) {
            let exportTemplateFileNames = [];
            for (const exportTemplateId of exportTemplatesToRunOnQuoteCreationData) {
                const exportTemplate = await fetchExportTemplateById(exportTemplateId);
                if (exportTemplate) {
                    exportTemplateFileNames.push(exportTemplate.name);
                }
            }
            if (exportTemplateFileNames.length > 0) {
                parsedExportTemplateFileNames = exportTemplateFileNames;
            }
        }
        return parsedExportTemplateFileNames;
    }
    
    const closeDialogBox = () => setIsSharepointFilesDialogOpened(false);

    const openDialogBox = async (e, isRegeneration) => {
        e.stopPropagation();

        if (isLoadingOpportunity) return;
        isRegenerationRef.current = isRegeneration;

        if (!isActive) {
            await generateFinalQuote(false);
            return;
        }

        const isValidSharepointFolder = opportunity.SharePoint_Folder__c && opportunity.SharePoint_Folder__c !== "null"
        const validInputFields = isValidSharepointFolder && quoteId != null && revision?.revisionNumber != null;
        if (validInputFields) setIsReadingSharepointFiles(true);

        let hasQuote = false;
        if (validInputFields && revision.isLocked) {
            const { message: quoteExists, responseOk } = await hasGeneratedQuote(opportunity.SharePoint_Folder__c, quoteId, revision.revisionNumber);
            hasQuote = responseOk && quoteExists;
        }
        hasGeneratedQuoteRef.current = hasQuote;

        if (!validInputFields || !isActive || (isRegeneration && !hasQuote)) {
            if (isRegeneration && !hasQuote) {
                toast.warn("The generated document could not be found. The operation will continue but no changes have been made to the LIVE folder");
            }
            setIsReadingSharepointFiles(false);
            await generateFinalQuote(false);
            return;
        }

        setIsReadingSharepointFiles(true);

        const quoteTemplateFileName = isRegeneration ? "" : getQuoteTemplateFileName();

        const parsedExportTemplateFileNames = await getExportTemplateFileNames();        

        const { message: liveMessage, responseOk: liveResponseOk } = await getOpportunityLiveFiles(opportunity.SharePoint_Folder__c, quoteId, revision.revisionNumber, quoteTemplateFileName, isRegeneration, parsedExportTemplateFileNames, false);
        if (!liveResponseOk) {
            toast.error(liveMessage);
            console.error(`Error retrieving live files - ${liveMessage}`);    
            setIsReadingSharepointFiles(false);
            await generateFinalQuote(false, false);        
            return;
        }

        setLiveFiles(liveMessage);
        setIsReadingSharepointFiles(false);

        setIsSharepointFilesDialogOpened(true)
    }

    const getQuoteTemplateFileName = () => {
        const opportunityName = sanitiseOpportunityName(opportunity?.Name, `Opportunity_${opportunityId}`);
        return getQuoteFileName(quoteRevision.quoteId, quoteRevision.revisionNumber, opportunityName);
    }

    const buildExcelFileName = (exportTemplateId) => {
        const exportTemplate = exportTemplateData.find(w => w.id === exportTemplateId)
        return exportTemplate ? `${quoteId}-${revision.revisionNumber} ${exportTemplate.name}.xlsx` : `${quoteId}-${revision.revisionNumber} ${exportTemplateId}_Not_Found.xlsx`;
    }

    const exportTemplateById = async (exportTemplateId) => {
        const updatedQuoteItems = quoteItems.map(item => {
            const itemLookupType = (itemLookupTypes ?? []).find(ilt => ilt.id === item.itemLookupTypeId);
            const supplier = (suppliers ?? []).find(s => s.id === item.supplierId);

            // Merge label/value pairs into one object
            const typeCustomAttributes = (item.typeCustomAttributes || []).reduce((acc, attr) => {
                acc[attr.label] = attr.value;
                return acc;
            }, {});

            return {
                ...item,
                ...typeCustomAttributes,
                itemLookupTypeName: itemLookupType?.itemLookupTypeName || null,
                supplierName: supplier?.supplierName || null,
            };
        });

        // generates a single Excel template using the given exportTemplateId
        const body = {
            quoteJson: updatedQuoteItems,
            templateId: exportTemplateId,
            quoteId: quoteId,
            revisionNumber: revision.revisionNumber
        };

        // get the generated Excel file in base 64
        return await exportTemplate(body);        
    }


    const exportExcelTemplatesToSharepoint = async () => {
        if (exportTemplatesToRunOnQuoteCreationData.length === 0) return true;

        let exportTemplateFiles = [];
        // create an array of export template files to upload to Sharepoint.
        // This is a circuit breaker - all files must be successfully retrieved in base 64, otherwise exits the method without exporting to Sharepoint and returning false
        for (const exportTemplateId of exportTemplatesToRunOnQuoteCreationData) {

            // get the generated Excel file in base64
            const { file, fileName } = await exportTemplateById(exportTemplateId);
            if (!file) {
                toast.info(`No file returned for templateId: ${exportTemplateId}`);
                return false; // exits the method returning false
            }

            exportTemplateFiles.push({ exportTemplateId, file, fileName });
        }

        for (const exportTemplateFile of exportTemplateFiles) {
            // convert base64 to File object
            let blob;

            try {
                const byteChars = atob(exportTemplateFile.file);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
                blob = new Uint8Array(byteNumbers);
            }
            catch (error) {
                toast.error(`Failed to generate export file`);
                return false;
            }

            const excelFileName = buildExcelFileName(exportTemplateFile.exportTemplateId);
            const excelFile = new File([blob], excelFileName, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", lastModified: Date.now() });

            // upload the Excel file to Sharepoint and download it locally if the upload fails
            const result = await exportTemplateFileToSharepoint(excelFile, true);
            if (!result) return false;
        }

        return true;
    }


    const exportExcelTemplateToSharepoint = async () => {
        setExporting(true);

        // get the generated Excel file in base 64
        const { file } = await exportTemplateById(form.templateId);
        if (!file) {
            toast.info("No file returned from tester.");
            return false;
        }

        let blob;

        // convert base64 to File object
        try {
            const byteChars = atob(file);
            const byteNumbers = new Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
            blob = new Uint8Array(byteNumbers);
        } catch (error) {
            toast.error(`Failed to generate export file`);
            return false;
        }

        const excelFileName = buildExcelFileName(form.templateId);
        const excelFile = new File([blob], excelFileName, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", lastModified: Date.now() });

        // upload the Excel file to Sharepoint
        var result = await exportTemplateFileToSharepoint(excelFile, true);
        setExporting(false);
        if (!result) return;
    }

    const sanitiseOpportunityName = (value, fallback) => {
        const sanitized = String(value ?? "")
            .replace(/[\\/:*?"<>|#%&{}~]/g, "-")
            .trim()
            .replace(/\s+/g, " ");

        return sanitized || fallback;
    };

    const generateQuoteAndFinalise = async (updateLiveFolder) => {
        const quoteTemplateFileName = getQuoteTemplateFileName();

        const body = {
            quoteRevisionId: revisionId,
            quoteItems: exportData,
            salesForceData: salesForceData,
            customContent: customContent,
            template: quoteTemplate
        };

        // get the generated Word file in base 64
        const generateQuoteResult = await generateQuote(body);
        if (generateQuoteResult === null) return ({ quoteExported: false, sentToSharepoint: false });

        let blob;

        // convert base64 to File object
        try {
            const byteChars = atob(generateQuoteResult.file);
            const byteNumbers = new Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
            blob = new Uint8Array(byteNumbers);
        } catch (error) {
            toast.error(`Please select a valid template before exporting`);
            return false;
        }

        const wordFile = new File([blob], quoteTemplateFileName, { type: blob.type, lastModified: Date.now() });

        // upload the Word template file to Sharepoint and return the Sharepoint link in the finaliseResult.message variable
        const finaliseResult = await finalise(wordFile, updateLiveFolder);
        if (!finaliseResult.responseOk) return { quoteExported: true, sentToSharepoint: false };

        return { quoteExported: true, sentToSharepoint: true, sharepointURL: finaliseResult.message };
    }

    const exportTemplateFileToSharepoint = async (file, open) => {
        const form = new FormData();
        form.append('file', file);
        form.append('sharepoint', opportunity.SharePoint_Folder__c);
        form.append('quoteId', quoteId);
        form.append('revisionNumber', revision.revisionNumber);
        const { message, responseOk } = await exportQuoteFile(form); // generate the excel file
        if (!responseOk) {
            toast.error(`${message} ${file.name}`);
            console.error(`Quote ${quoteId} revision ${revision.revisionNumber} - Error uploading the export file ${file.name} to Sharepoint: ${message}`);
            await createAudit({ "action": "Upload sharepoint export file", "result": "Error", "source": `quote.${quoteId}`, "payload": JSON.stringify({ message: `Error uploading the export file ${file.name} to Sharepoint: ${message}` }) });
            if (open) downloadFile(file, file.name);
            return false;
        }
        else {
            await createAudit({ "action": "Upload sharepoint export file", "result": "Successful", "source": `quote.${quoteId}`, "payload": JSON.stringify({ sharepointFile: message }) });
            if (open) window.open(message, '_blank', 'noopener,noreferrer');
            return true;
        }
    }

    const getExportTemplates = async () => {
        const exportTemplates = await fetchExportTemplateDropdown();
        return exportTemplates;
    }

    const {
        data: exportTemplateData,
    } = useQuery({
        queryKey: ["exportTemplatesDropdown"],
        queryFn: getExportTemplates,
        staleTime: 60000
    });

    const exportTemplateOptions = useMemo(
        () => (exportTemplateData ?? []).map(r => ({ label: r.name, value: r.id })),
        [exportTemplateData]
    );

    const selectedExportTemplate = useMemo(
        () => exportTemplateOptions.find(o => o.value === form.templateId) ?? null,
        [exportTemplateOptions, form.templateId]
    );

    const quoteTemplate = useMemo(
        () => templatesData.find(o => o.id === quoteRevision.quoteTemplateId) ?? null,
        [templatesData, quoteRevision.quoteTemplateId]
    );

    const {
        data: exportTemplatesToRunOnQuoteCreationData
    } = useQuery({
        queryKey: ["exportTemplatesToRunOnQuoteCreation"],
        queryFn: fetchExportTemplatesToRunOnQuoteCreation,
        staleTime: 60000
    });

    const selectStyle = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        })
    };

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        },
    };

    const validatedQuoteItems = useMemo(() => quoteItems.map(x => ({
        ...x,
        validationErrors: (getItemErrors(x, salesforceCodes) || [])
    })), [quoteItems, salesforceCodes]);

    const quoteItemsWithErrors = useMemo(() => {
        return validatedQuoteItems
            .filter(i => i.validationErrors.some(x => x.severity === "error"))
            .map(i => ({
                ...i,
                validationErrors: i.validationErrors.filter(x => x.severity === "error")
            }))
    }, [validatedQuoteItems]);

    const quoteItemsWithWarnings = useMemo(() => {
        return validatedQuoteItems
            .filter(i => i.validationErrors.some(x => x.severity === "warning"))
            .map(i => ({
                ...i,
                validationErrors: i.validationErrors.filter(x => x.severity === "warning")
            }))
    }, [validatedQuoteItems]);

    const canFinalise = useMemo(() => {
        return quoteItemsWithErrors.length === 0 && warnings.filter(x => !x.acknowledged).length === 0;
    }, [quoteItemsWithErrors, warnings]);

    const isMainAndUnlocked = () => {
        return revision?.isMain && !revisionIsLocked;
    }

    return (!revisionsLoading && !opportunityLoading &&
        <Container fluid>
            <Row className="g-3">
                {/* Left panel — 2/3 on large screens, full width on medium/smaller */}
                <Col xs="12" xl="8">
                    <Card className="mb-0">
                        <CardBody>
                            <h3 className="mb-5">Quote Review</h3>
                            {quoteItemsWithErrors.length > 0 && <QuoteReviewErrors
                                quoteItems={quoteItemsWithErrors}
                            />
                            }
                            <QuoteReviewWarnings
                                warnings={quoteRevision.quoteOrderInformation?.warnings || []}
                                quoteItems={quoteItemsWithWarnings}
                                quoteModules={quoteModules}
                                quoteCustomisation={quoteCustomisation}
                                onAddWarning={onAddWarning}
                                onRemoveWarning={onRemoveWarning}
                                onAcknowledgedWarningChange={onAcknowledgedWarningChange}
                                onApprovedWarningChange={onApprovedWarningChange}
                            />
                        </CardBody>
                    </Card>
                </Col>

                {/* Right panel — 1/3 on large screens, below on smaller */}
                <Col xs="12" xl="4">
                    <>
                        <Row className="g-3">
                            <Col xs="12" lg="4" xl="12" xxl="12">
                                <Card className="mb-0">
                                    <CardHeader className="mt-2">Generate Export</CardHeader>
                                    <hr className="mt-2" />
                                    <CardBody>
                                        <div className="d-flex gap-2 justify-start">
                                            <Select
                                                inputId="templateId"
                                                name="templateId"
                                                value={selectedExportTemplate}
                                                options={exportTemplateOptions}
                                                onChange={(opt) => {
                                                    setForm({ ...form, templateId: opt?.value ?? undefined });
                                                }}
                                                placeholder="Select Export Template..."
                                                theme={customTheme}
                                                styles={{
                                                    ...selectStyle,
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    menu: base => ({ ...base, zIndex: 9999 })
                                                }}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                components={{ MenuList: CustomMenuList }}
                                                className="w-100"
                                            />
                                            <Button disabled={!selectedExportTemplate || exporting} onClick={exportExcelTemplateToSharepoint}>{exporting ? <Spinner /> : "Export"}</Button>
                                        </div>
                                        <br />
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col xs="12" lg="4" xl="12" xxl="12">
                                <Card className="mb-0">
                                    <CardHeader className="mt-2">Finalise</CardHeader>
                                    <hr className="mt-2" />
                                    {!canFinalise &&
                                        <CardBody className={`bg-warning-subtle border border-warning rounded-3 m-3`} style={{ borderRadius: '4px' }}>
                                            <Row>
                                                <Col xs="auto">
                                                    <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                                                </Col>
                                                <Col>
                                                    <span style={{ color: "black" }}>There are errors and/or unacknowledged warnings that must be resolved before the quote can be finalised.</span>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    }
                                    <CardBody className="">
                                        <p>Review your quote details and finalize.</p>
                                        <div className="d-flex justify-content-between align-items-center mt-4">
                                            <div className="d-flex gap-2">
                                                {revisionIsLocked ? (
                                                    <span title={
                                                        isBasketProcessingActive ? "Basket processing needs to run — please wait" :
                                                        !isGenerateFinalQuoteButtonEnabled ? "Quote regeneration is already in progress" :
                                                        (lockState?.isLocked && !lockState?.lockOverride?.isOn) ? "The quote is locked by another user and cannot be regenerated" :
                                                        !revision?.isMain ? "Only the main revision can be regenerated" :
                                                        !canFinalise ? "There are errors or unacknowledged warnings that must be resolved first" :
                                                        undefined
                                                    }>
                                                        <Button
                                                            disabled={!(revision?.isMain && canFinalise && !(lockState?.isLocked && !lockState?.lockOverride?.isOn) && isGenerateFinalQuoteButtonEnabled && !isBasketProcessingActive)}
                                                            onClick={(e) => openDialogBox(e, true)}>
                                                            Regenerate Quote
                                                        </Button>
                                                    </span>
                                                ) : (
                                                    <span title={
                                                        isBasketProcessingActive ? "Basket processing needs to run — please wait" :
                                                        !isGenerateFinalQuoteButtonEnabled ? "Quote generation is already in progress" :
                                                        lockState?.isLocked ? "The quote is locked and cannot be finalised" :
                                                        !isMainAndUnlocked() ? (!revision?.isMain ? "Only the main revision can be finalised" : "This revision is already locked") :
                                                        !canFinalise ? "There are errors or unacknowledged warnings that must be resolved first" :
                                                        undefined
                                                    }>
                                                        <Button
                                                            disabled={!(canFinalise && isMainAndUnlocked() && !lockState?.isLocked && isGenerateFinalQuoteButtonEnabled && !isBasketProcessingActive)}
                                                            onClick={(e) => openDialogBox(e, false)}>
                                                            Generate Final Quote
                                                        </Button>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <br />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                </Col>
            </Row>
            <Modal isOpen={isGeneratingFinalQuote} centered size="lg">
                <DataLoader message="Generating Final Quote..." />
            </Modal>
            <QuoteLiveFilesDialog
                isDialogBoxOpened={isSharepointFilesDialogOpened}
                liveFiles={liveFiles}
                closeDialogBox={closeDialogBox}
                skipButtonAction={() => generateFinalQuote(false)}
                updateLiveButtonAction={() => generateFinalQuote(true)}
            />
            <Modal isOpen={isReadingSharepointFiles} centered size="lg">
                <DataLoader showLoadingTitle={false} message="Reading files from Sharepoint..." />
            </Modal>
        </Container>
    );
}